import { estCheminLienFichierConciergerie } from './conciergerie-liens.js'

/**
 * Redirections autorisées vers l'espace formateur après login.
 * Source unique partagée entre :
 *   - guards.js (réécriture du redirect après login si déjà authentifié)
 *   - FormateurLogin.vue (redirection après login)
 *   - FormateurInscriptionForm.vue (mémorise l'action en localStorage)
 *   - FormateurEmailVerification.vue (relit l'action depuis localStorage)
 *
 * Pour ajouter un nouveau redirect : ajouter une entrée dans FORMATEUR_REDIRECTS.
 */
// « publier-une-mission » a été retiré : la page a déménagé chez Global
// Prévention avec la scission, et menait au message « cette rubrique a
// déménagé » côté espace Alertis. Un ancien lien ou une action en attente
// (localStorage) tombe simplement sur homePath — cf. resolveFormateurRedirectPath.
export const FORMATEUR_REDIRECTS = {}

export const PENDING_ACTION_STORAGE_KEY = 'alertis_formateur_post_login_redirect'

const PENDING_ACTION_TTL_MS = 30 * 60 * 1000

export function isKnownFormateurRedirect(key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(FORMATEUR_REDIRECTS, key)
}

export function resolveFormateurRedirectPath(key) {
  // Le permalien d'un fichier de conciergerie revient après connexion : c'est
  // le seul chemin libre accepté, et seulement sous sa forme exacte — une
  // redirection ouverte à n'importe quelle adresse enverrait où l'on veut.
  if (estCheminLienFichierConciergerie(key)) return key
  return isKnownFormateurRedirect(key) ? FORMATEUR_REDIRECTS[key] : null
}

export function savePendingFormateurAction(key) {
  if (!isKnownFormateurRedirect(key)) return
  try {
    const payload = JSON.stringify({ value: key, expiresAt: Date.now() + PENDING_ACTION_TTL_MS })
    localStorage.setItem(PENDING_ACTION_STORAGE_KEY, payload)
  } catch {}
}

export function readPendingFormateurAction() {
  try {
    const raw = localStorage.getItem(PENDING_ACTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      clearPendingFormateurAction()
      return null
    }
    if (typeof parsed.expiresAt !== 'number' || Date.now() > parsed.expiresAt) {
      clearPendingFormateurAction()
      return null
    }
    return isKnownFormateurRedirect(parsed.value) ? parsed.value : null
  } catch {
    clearPendingFormateurAction()
    return null
  }
}

export function clearPendingFormateurAction() {
  try { localStorage.removeItem(PENDING_ACTION_STORAGE_KEY) } catch {}
}

/**
 * La page d'accueil de l'espace formateur pour CE compte.
 *
 * Un formateur atterrit sur son tableau de bord. Un compte ouvert par la
 * conciergerie n'a ni formations, ni calendrier, ni contrats : le tableau de
 * bord lui rendrait des blocs vides, et ses appels seraient refusés par le
 * serveur (`refuserConciergerieSeule`). Il atterrit donc sur la seule rubrique
 * qui le concerne — celle que sa navigation affiche déjà seule.
 *
 * Source unique partagée par l'écran de connexion et les gardes de route : deux
 * accueils calculés séparément, c'est un des deux qui finit par envoyer
 * quelqu'un sur une page vide.
 *
 * @param {{ accesConciergerieUniquement?: boolean }|null} formateur
 */
export function formateurHomePath(formateur) {
  return formateur?.accesConciergerieUniquement
    ? '/espace-formateur/conciergerie'
    : '/espace-formateur/tableau-de-bord'
}

/** Vrai pour une rubrique ouverte à un compte de conciergerie. */
export function estRubriqueConciergerie(path) {
  return String(path || '').startsWith('/espace-formateur/conciergerie')
}
